import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'wpp-theme';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function getStored(): Theme {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

function apply(theme: Theme) {
  const dark = theme === 'dark' || (theme === 'system' && media().matches);
  document.documentElement.classList.toggle('dark', dark);
}

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

/** 'system' honours prefers-reduced-motion; 'always' overrides it (AB#930 nav icons et al). */
export type Motion = 'system' | 'always';

const MOTION_KEY = 'wpp-motion';

function applyMotion(motion: Motion) {
  const el = document.documentElement;
  if (motion === 'always') el.setAttribute('data-motion', 'always');
  else el.removeAttribute('data-motion');
}

interface MotionState {
  motion: Motion;
  setMotion: (m: Motion) => void;
}

// Lives beside the theme store so it is applied at the same moment: this module
// is pulled in eagerly by the route tree, before first paint.
export const useMotionStore = create<MotionState>((set) => {
  const initial: Motion = localStorage.getItem(MOTION_KEY) === 'always' ? 'always' : 'system';
  applyMotion(initial);

  return {
    motion: initial,
    setMotion: (m) => {
      localStorage.setItem(MOTION_KEY, m);
      applyMotion(m);
      set({ motion: m });
    },
  };
});

export const useThemeStore = create<ThemeState>((set) => {
  const initial = getStored();
  apply(initial);

  // Follow the OS while in 'system' mode.
  media().addEventListener('change', () => {
    if (useThemeStore.getState().theme === 'system') apply('system');
  });

  return {
    theme: initial,
    setTheme: (t) => {
      localStorage.setItem(STORAGE_KEY, t);
      apply(t);
      set({ theme: t });
    },
  };
});
